import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, List, ListItem, ListItemText, Divider, Button, Skeleton } from '@mui/material';
import { get } from 'lodash';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Schedule as ScheduleIcon } from '@mui/icons-material';
import Bluebird from 'bluebird';
import moment from 'moment';
import { getUserFullName } from '../../../helpers/user';
import { formatAddress, formatTime } from '../../../helpers/formatters';
import { Colors } from '../../../assets/theme/Colors';
import ReceiverChip from '../../../components/ReceiverChip';
import AdopterSelector from '../../../components/AdopterSelector';
import { createEventShiftRegistration, deleteRescuerEventShiftRegistration } from '../../../api/events';
import useActiveUser from '../../../hooks/useActiveUser';
import errorMessages from '../../../assets/errorMessages';
import { fetchSiteEventsList } from '../../../actions/sites';
import useActiveSite from '../../../hooks/useActiveSite';
import OverlayLoader from '../../../components/OverlayLoader';
import snackbarHelper from '../../../helpers/snackbarHelper';

const agendaHeaderSx = {
  boxShadow: 25,
  padding: 2,
  display: 'flex',
  flexFlow: 'row wrap',
  '& > *:first-child': {
    marginRight: 3,
  },
  '& > *:not(:first-child):not(:last-child)': {
    marginRight: 1,
  },
  alignItems: 'center',
};

export const EventTimelineRegistrations = ({ registrations, rescuers, shift, eventId, setIsSubmitting }) => {
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const dispatch = useDispatch();

  const handleRescuerSelect = (rescuerId) => {
    setIsSubmitting(true);
    return Bluebird.try(() =>
      createEventShiftRegistration(eventId, shift.id, {
        user_id: rescuerId,
        created_by_id: activeUser.id,
      })
    )
      .then(() => dispatch(fetchSiteEventsList(activeSite.id)))
      .then(() => snackbarHelper.success('Rescuer assigned to the shift'))
      .catch((error) => {
        switch (error.code) {
          case errorMessages.ERR_EVENT_SHIFT_HAS_NO_FREE_SLOTS_LEFT.code:
            snackbarHelper.info(errorMessages.ERR_EVENT_SHIFT_HAS_NO_FREE_SLOTS_LEFT.message);
            break;
          case errorMessages.ERR_USER_ALREADY_REGISTERED_FOR_EVENT_SHIFT.code:
            snackbarHelper.info(errorMessages.ERR_USER_ALREADY_REGISTERED_FOR_EVENT_SHIFT.message);
            break;
          default:
            snackbarHelper.error(error.message);
            break;
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleRemoveRescuer = (rescuerId) => {
    setIsSubmitting(true);
    return Bluebird.try(() => deleteRescuerEventShiftRegistration(rescuerId, eventId, shift.id))
      .then(() => dispatch(fetchSiteEventsList(activeSite.id)))
      .then(() => snackbarHelper.success('Rescuer removed from the shift'))
      .catch((error) => snackbarHelper.error(error.message))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <List>
      {registrations.map(({ id, user }, index, { length }) => (
        <React.Fragment key={id}>
          <ListItem dense alignItems="flex-start">
            <ListItemText
              disableTypography
              primary={
                <ReceiverChip
                  value={getUserFullName(user)}
                  onClick={() => handleRemoveRescuer(user.id, id)}
                />
              }
            />
          </ListItem>
          {length - 1 !== index && <Divider />}
        </React.Fragment>
      ))}
      {registrations.length === 0 && (
        <ListItem>
          <ListItemText
            disableTypography
            primary={
              <Typography variant="body2" color="textSecondary">
                No volunteers assigned
              </Typography>
            }
          />
        </ListItem>
      )}
      {(registrations.length < shift?.max_volunteers || shift?.max_volunteers === null) && (
        <>
          <Divider />
          <ListItem>
            <Box width="100%">
              <AdopterSelector
                value=""
                placeholder="Select rescuer"
                noOptionsText="There are no active rescuers in the Site"
                rescuersList={rescuers}
                onChange={({ value }) => handleRescuerSelect(value.id)}
              />
            </Box>
          </ListItem>
        </>
      )}
    </List>
  );
};

const EventAgendaView = ({ eventsList, inflight, rescuers }) => {
  const [isSubmiting, setIsSubmitting] = useState(false);
  const isLoading = inflight;
  const [index, setIndex] = useState(0);

  if (!isLoading && !eventsList.length) {
    return (
      <Box sx={agendaHeaderSx}>
        <Typography variant="subtitle1">No upcoming events</Typography>
      </Box>
    );
  }

  const eventTime = isLoading ? (
    <Skeleton width="100px" />
  ) : (
    <strong>{`${formatTime(eventsList[index].begin.value)} - ${formatTime(eventsList[index].end.value)}`}</strong>
  );

  const eventAddress = isLoading ? (
    <Typography variant="subtitle1">
      <Skeleton width="340px" />
    </Typography>
  ) : (
    <>
      <Typography variant="subtitle1">{eventTime}</Typography>
      <Typography variant="subtitle1">
        <strong>
          {eventsList[index].name}, {moment(eventsList[index].date.value).format('M/DD')},
        </strong>
      </Typography>
      <Typography variant="subtitle1">{formatAddress(eventsList[index].location)}</Typography>
    </>
  );

  return (
    <OverlayLoader isLoading={isSubmiting}>
      <Box sx={agendaHeaderSx}>
        {eventAddress}
        {eventsList.length > 1 && (
          <Box display="flex" justifyContent="flex-end" flexGrow={1}>
            <Button disabled={index === 0} onClick={() => setIndex(index - 1)}>
              Previous
            </Button>
            <Button disabled={index === eventsList.length - 1} onClick={() => setIndex(index + 1)}>
              Next
            </Button>
          </Box>
        )}
      </Box>
      <Timeline sx={{ padding: 0 }}>
        {get(eventsList[index], 'shifts', []).map((shift, shiftIndex, { length }) => {
          const registrations = get(shift, 'registrations', []).filter((r) => r.deleted_at === null);
          return (
            <TimelineItem
              sx={{
                '&:before': {
                  display: 'none',
                },
              }}
              key={shift.id}
            >
              <TimelineSeparator>
                <TimelineDot sx={{ marginTop: 0 }}>
                  <ScheduleIcon />
                </TimelineDot>
                {length - 1 !== shiftIndex && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent sx={(theme) => ({ padding: theme.spacing(0, 0, 2, 2) })}>
                <Box boxShadow={25}>
                  <Box bgcolor={Colors.secondaryColor} p={1} color="white">
                    <Typography variant="body1">
                      <strong>{`${formatTime(shift.begin.value)} - ${formatTime(shift.end.value)}`}</strong>
                    </Typography>
                  </Box>
                  <Box px={1}>
                    <EventTimelineRegistrations
                      registrations={registrations}
                      rescuers={rescuers}
                      shift={shift}
                      eventId={eventsList[index].id}
                      setIsSubmitting={setIsSubmitting}
                    />
                  </Box>
                </Box>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </OverlayLoader>
  );
};

export default EventAgendaView;
